import { Dropdown, IDropdownOption, IDropdownStyles, IconButton, TooltipHost } from "@fluentui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NameIcons } from "../../../../../assets/icons";
import { LabelWithInfo } from "../../../../common/labels/label";
import { TextInputStyled } from "../../../../common/inputs/textInput.jss";
import { fetchCertificates } from "../../../../../redux/actions/catalog/catalog";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { mapIDescriptionToDropDownOptions, numericDefaultValue } from "../../../../../utils";
import { MultiSelectorStyled } from "../../../../common/selectors/multiSelectors/multiSelector.jss";

interface ISelectorTipoCertificadoProps {
    selectedKey?: number;
    onChange: (certificateTypeId: number | undefined) => void;
    disabled?: boolean;
}

/**
 * SelectorTipoCertificado — selección única del tipo de certificado activo usado para
 * condicionar la ordenación por Nº documento (RN-001, RN-002). Ver Especificaciones funcionales.pdf.
 * Reutiliza exactamente el mismo patrón visual (contenedor + LabelWithInfo) que el campo
 * "Nº Expediente/UTI" (ver components/request/filter/container/.../others/others.tsx).
 */
export const SelectorTipoCertificado: React.FC<ISelectorTipoCertificadoProps> = ({ selectedKey, onChange, disabled }) => {
    const { data, loading } = useAppSelector((store) => store.catalog.certificates);
    const dispatch = useAppDispatch();
    const { t } = useTranslation("common", { keyPrefix: "REQUEST_FILTER.FILTER" });
    const { t: tTooltip } = useTranslation("common", { keyPrefix: "LIST.TOOLTIP_DESCRIPTION" });
    const multiSelectorStyled = MultiSelectorStyled({ isDisabled: disabled || loading, isFormSelector: false });
    const textInputStyled = TextInputStyled();
    const [isCalloutVisible, setCalloutVisibility] = useState<boolean>(false);

    const loadCertificates = () => dispatch(fetchCertificates());

    useEffect(() => {
        data.length === 0 && loadCertificates();
    }, [dispatch]);

    const isEmpty = useMemo(() => !loading && data.length === 0, [loading, data]);

    const options: IDropdownOption[] = useMemo(
        () => [numericDefaultValue, ...mapIDescriptionToDropDownOptions(data)],
        [data],
    );

    const styles: Partial<IDropdownStyles> = {
        root: multiSelectorStyled.rootStyles,
        callout: multiSelectorStyled.callout,
        dropdownItem: multiSelectorStyled.optionText,
        dropdownItemSelected: multiSelectorStyled.optionText,
        title: multiSelectorStyled.inputText,
    };

    const handleChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        if (!option) return;
        const isAllOption = String(option.key) === String(numericDefaultValue.key);
        onChange(isAllOption ? undefined : Number(option.key));
    };

    const selectedOptionTooltip = useMemo(() => {
        return options.find((item) => String(item.key) === String(selectedKey ?? numericDefaultValue.key))?.text;
    }, [options, selectedKey]);

    const filled = selectedKey !== undefined ? "filled" : "";

    return (
        <div
            className={`${textInputStyled.input} ${textInputStyled.withCallOut}`}
            title={selectedOptionTooltip}
        >
            <Dropdown
                options={options}
                selectedKey={selectedKey ?? Number(numericDefaultValue.key)}
                onChange={handleChange}
                disabled={disabled || loading || isEmpty}
                styles={styles}
                key={`documentSortCertificate-${SelectorTipoCertificado.name}`}
            />
            <LabelWithInfo
                title={t("DOCUMENT_SORT_CERTIFICATE")}
                calloutMessage={t("INFO.DOCUMENT_SORT_CERTIFICATE")}
                isCalloutVisible={isCalloutVisible}
                setCalloutVisibility={setCalloutVisibility}
                className={filled}
            />
            {isEmpty && (
                <TooltipHost content={tTooltip("DOCUMENT_SORT_DISABLED")}>
                    <IconButton
                        iconProps={{ iconName: NameIcons.RELOAD }}
                        onClick={loadCertificates}
                        ariaLabel={tTooltip("DOCUMENT_SORT_DISABLED")}
                    />
                </TooltipHost>
            )}
        </div>
    );
};
